import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Certificate } from '../../models/certificate.model';

export type FileType = 'pdf' | 'other';
export type CertificateType = 'token' | 'p10';

export interface SignState {
  // Configuración de firma
  fileType: FileType;
  certificateType: CertificateType;
  
  // Archivos
  files: File[];
  pdfBase64s: string[];
  cadesBase64s: string[];
  signedFiles: string[];
  
  // Certificados
  certificates: Certificate[];
  selectedCertificate: Certificate | null;
  uploadedCertificate: string | null;
  uploadedCertificatePassword: string | null;
  
  // Credenciales (token virtual)
  user: string;
  password: string;
  
  // Estados
  isProcessing: boolean;
  isSigned: boolean;
  showPdfViewer: boolean;
  currentViewerIndex: number;
  error: string | null;
}

const initialState: SignState = {
  fileType: 'pdf',
  certificateType: 'token',
  files: [],
  pdfBase64s: [],
  cadesBase64s: [],
  signedFiles: [],
  certificates: [],
  selectedCertificate: null,
  uploadedCertificate: null,
  uploadedCertificatePassword: null,
  user: '',
  password: '',
  isProcessing: false,
  isSigned: false,
  showPdfViewer: false,
  currentViewerIndex: 0,
  error: null
};

@Injectable({
  providedIn: 'root'
})
export class SignStateService {
  private state = new BehaviorSubject<SignState>(initialState);
  
  /**
   * Observable del estado completo
   */
  public state$: Observable<SignState> = this.state.asObservable();

  /**
   * Obtiene el estado actual de forma síncrona
   */
  get currentState(): SignState {
    return this.state.value;
  }

  // ==========================================
  // ACCIONES DE CONFIGURACIÓN
  // ==========================================

  /**
   * Establece el tipo de archivo (PDF o Otro)
   */
  setFileType(fileType: FileType): void {
    this.state.next({
      ...this.currentState,
      fileType,
      files: [],
      pdfBase64s: [],
      cadesBase64s: [],
      signedFiles: [],
      isSigned: false,
      showPdfViewer: false,
      certificates: [],
      selectedCertificate: null,
      uploadedCertificate: null,
      uploadedCertificatePassword: null,
      user: '',
      password: ''
    });
  }

  /**
   * Establece el tipo de certificado (Token o P10)
   */
  setCertificateType(certificateType: CertificateType): void {
    this.state.next({
      ...this.currentState,
      certificateType,
      certificates: [],
      selectedCertificate: null,
      uploadedCertificate: null,
      uploadedCertificatePassword: null,
      user: '',
      password: '',
      isSigned: false,
      signedFiles: [],
      showPdfViewer: false
    });
  }

  // ==========================================
  // ACCIONES DE ARCHIVOS
  // ==========================================

  /**
   * Agrega archivos a la lista
   */
  setFiles(files: File[]): void {
    this.state.next({
      ...this.currentState,
      files,
      isSigned: false
    });
  }

  /**
   * Elimina un archivo por índice
   */
  removeFile(index: number): void {
    const files = [...this.currentState.files];
    files.splice(index, 1);
    
    const pdfBase64s = [...this.currentState.pdfBase64s];
    const cadesBase64s = [...this.currentState.cadesBase64s];
    
    if (this.currentState.fileType === 'pdf') {
      pdfBase64s.splice(index, 1);
    } else {
      cadesBase64s.splice(index, 1);
    }

    this.state.next({
      ...this.currentState,
      files,
      pdfBase64s,
      cadesBase64s
    });
  }

  /**
   * Establece los archivos en base64
   */
  setPdfBase64s(pdfBase64s: string[]): void {
    this.state.next({
      ...this.currentState,
      pdfBase64s
    });
  }

  /**
   * Establece los archivos CAdES en base64
   */
  setCadesBase64s(cadesBase64s: string[]): void {
    this.state.next({
      ...this.currentState,
      cadesBase64s
    });
  }

  /**
   * Establece los archivos firmados
   */
  setSignedFiles(signedFiles: string[]): void {
    this.state.next({
      ...this.currentState,
      signedFiles,
      isSigned: true
    });
  }

  // ==========================================
  // ACCIONES DE CERTIFICADOS
  // ==========================================

  /**
   * Establece la lista de certificados disponibles
   */
  setCertificates(certificates: Certificate[]): void {
    this.state.next({
      ...this.currentState,
      certificates
    });
  }

  /**
   * Selecciona un certificado
   */
  selectCertificate(certificate: Certificate): void {
    this.state.next({
      ...this.currentState,
      selectedCertificate: certificate
    });
  }

  /**
   * Establece el certificado P10 cargado
   */
  setUploadedCertificate(certificateBase64: string, password: string): void {
    this.state.next({
      ...this.currentState,
      uploadedCertificate: certificateBase64,
      uploadedCertificatePassword: password
    });
  }

  /** Limpia certificado P10 / PFX cargado (p. ej. tras contraseña incorrecta). */
  clearUploadedCertificate(): void {
    this.state.next({
      ...this.currentState,
      uploadedCertificate: null,
      uploadedCertificatePassword: null
    });
  }

  // ==========================================
  // ACCIONES DE CREDENCIALES
  // ==========================================

  /**
   * Establece las credenciales del usuario
   */
  setCredentials(user: string, password: string): void {
    this.state.next({
      ...this.currentState,
      user,
      password
    });
  }

  // ==========================================
  // ACCIONES DE ESTADOS
  // ==========================================

  /**
   * Establece el estado de procesamiento
   */
  setProcessing(isProcessing: boolean): void {
    this.state.next({
      ...this.currentState,
      isProcessing
    });
  }

  /**
   * Establece si hay archivos firmados
   */
  setSigned(isSigned: boolean): void {
    this.state.next({
      ...this.currentState,
      isSigned
    });
  }

  /**
   * Muestra/oculta el visor de PDF
   */
  setShowPdfViewer(show: boolean, index: number = 0): void {
    this.state.next({
      ...this.currentState,
      showPdfViewer: show,
      currentViewerIndex: index
    });
  }

  /**
   * Establece un error
   */
  setError(error: string | null): void {
    this.state.next({
      ...this.currentState,
      error
    });
  }

  // ==========================================
  // ACCIONES GLOBALES
  // ==========================================

  /**
   * Resetea el estado completo
   */
  reset(): void {
    this.state.next(initialState);
  }

  /**
   * Resetea solo la sección de firma
   */
  resetSignSection(): void {
    this.state.next({
      ...this.currentState,
      files: [],
      pdfBase64s: [],
      cadesBase64s: [],
      signedFiles: [],
      isSigned: false,
      showPdfViewer: false,
      currentViewerIndex: 0,
      certificates: [],
      selectedCertificate: null,
      uploadedCertificate: null,
      uploadedCertificatePassword: null,
      user: '',
      password: '',
      error: null
    });
  }
}

